import { useQuery } from "@tanstack/react-query";

import { customInstanceBpm } from "../axios-client";

export interface OrganizationDashboardItem {
    organizationKey: string;
    organizationName: string;
    processInstancesStarted: number;
    processInstancesCompleted: number;
    tasksStarted: number;
    tasksCompleted: number;
    completionRate: number;
    activeProcessInstances: number;
    pendingTasks: number;
    overdueTasks: number;
    incidentsCount: number;
    averageProcessDurationInMillis: number;
    slaComplianceRate: number;
    lastMovementAt?: string;
}

export interface OrganizationDashboardTrend {
    startDate: string;
    endDate: string;
    processInstancesStarted: number;
    processInstancesCompleted: number;
    tasksStarted: number;
    tasksCompleted: number;
}

export interface OrganizationDashboardData {
    periodStartDate: string;
    periodEndDate: string;
    organizationsCount: number;
    processInstancesStarted: number;
    processInstancesCompleted: number;
    tasksStarted: number;
    tasksCompleted: number;
    completionRate: number;
    activeProcessInstances: number;
    pendingTasks: number;
    overdueTasks: number;
    incidentsCount: number;
    averageProcessDurationInMillis: number;
    slaComplianceRate: number;
    organizationsWithoutMovement: number;
    trendAggregation: "DAY" | "WEEK" | "MONTH";
    trend: OrganizationDashboardTrend[];
    organizations: OrganizationDashboardItem[];
}

export interface OrganizationDashboardUser {
    userId?: string;
    userName: string;
    userEmail?: string;
    processInstancesStarted: number;
    activeProcessInstances: number;
    processInstancesCompleted: number;
    reworkedProcessInstances: number;
    reworkRate: number;
    completionRate: number;
    averageProcessDurationInMillis: number;
}

export interface OrganizationDashboardUsersData {
    organizationKey: string;
    organizationName: string;
    periodStartDate: string;
    periodEndDate: string;
    users: OrganizationDashboardUser[];
}

export interface OrganizationDashboardParams {
    startDate: string;
    endDate: string;
    timeZone?: string;
    organizationKeys?: string[];
}

export const getOrganizationDashboard = ({
    startDate,
    endDate,
    timeZone,
    organizationKeys = [],
}: OrganizationDashboardParams) => {
    const query = new URLSearchParams({ startDate, endDate });
    if (timeZone) query.set("timeZone", timeZone);
    organizationKeys.forEach((key) => query.append("organizationKeys", key));

    return customInstanceBpm<OrganizationDashboardData>(
        `/administrative/organization-dashboard?${query.toString()}`,
        { method: "GET" },
    );
};

export const useGetOrganizationDashboard = (
    params: OrganizationDashboardParams,
    enabled: boolean,
) =>
    useQuery({
        queryKey: ["organization-dashboard", params],
        queryFn: () => getOrganizationDashboard(params),
        enabled,
        staleTime: 60 * 1000,
    });

export const getOrganizationDashboardUsers = ({
    organizationKey,
    startDate,
    endDate,
    timeZone,
}: OrganizationDashboardParams & { organizationKey: string }) => {
    const query = new URLSearchParams({ startDate, endDate });
    if (timeZone) query.set("timeZone", timeZone);

    return customInstanceBpm<OrganizationDashboardUsersData>(
        `/administrative/organization-dashboard/${encodeURIComponent(organizationKey)}/users?${query.toString()}`,
        { method: "GET" },
    );
};

export const useGetOrganizationDashboardUsers = (
    params: OrganizationDashboardParams & { organizationKey: string },
    enabled: boolean,
) =>
    useQuery({
        queryKey: ["organization-dashboard-users", params],
        queryFn: () => getOrganizationDashboardUsers(params),
        enabled,
        staleTime: 60 * 1000,
    });
